/**
 * Component-specific styling definitions
 * 
 * Contains styles for specific components that are used frequently
 */

// =============================================================================
// HEADER COMPONENT STYLES
// =============================================================================

export const headerStyles = {
  // Main header container
  // --header-radius: the rounded bottom, also used by the clip-path that
  // shrinks the header on scrolled machine and line pages (Header.tsx)
  // Sticky, z-index and the shadow are on the wrapper (stickyWrapper): the
  // clip-path below would cut a box-shadow off.
  container: 'pointer-events-auto [--header-radius:1.25rem] sm:[--header-radius:2.5rem] rounded-b-[var(--header-radius)] px-3 sm:px-10 flex flex-col overflow-hidden',
  // drop-shadow follows the clipped, rounded shape; from lg up it falls on
  // the menu rail, which runs on under the rounded corner. The wrapper keeps
  // the full header's height (280 px with a card) while a scrolled header
  // shows 120 px: pointer-events-none lets clicks below the visible header
  // reach the menu and the page (the header itself takes them again)
  stickyWrapper: 'pointer-events-none sticky top-0 z-50 drop-shadow-[0_6px_12px_rgba(15,23,42,0.22)]',
  // Background: var(--header-gradient) (tokens.css, per-org branding)
  
  // Top bar
  topBar: {
    // fixed height (HEADER_HEIGHT.bar in Header.tsx): its items sit in the
    // middle of the bar, and a card below starts under it
    container: 'flex shrink-0 items-center justify-between relative w-full',
    left: 'flex items-center gap-4',
    center: 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-4',
    right: 'flex gap-2 text-on-header text-2xl ml-auto items-center',
    title: 'text-on-header text-xl font-semibold',
  },
  
  // Machine card states
  machineCard: {
    base: 'relative flex flex-col md:flex-row items-center justify-center transition-all duration-300 ease-out origin-top',
    // the one-line row of the scrolled header (HEADER_HEIGHT.compact - bar)
    scrolled: 'scale-100 opacity-100 h-10',
    normal: 'scale-100 opacity-100 min-h-[100px] md:min-h-[140px] py-3 md:py-6',
    
    info: {
      container: 'flex-1 flex justify-center md:justify-start items-center order-2 md:order-1',
      compact: 'flex items-center gap-4 text-on-header text-sm transition-all duration-300 ease-out',
      title: 'font-bold',
      subtitle: 'flex items-center gap-3 text-xs',
    },
    
    picture: {
      container: 'absolute left-1/2 top-[60%] z-10',
      style: { x: '-50%', y: '-50%' },
    },
  },
  
  // Line card states  
  lineCard: {
    base: 'relative flex flex-col md:flex-row items-center transition-all duration-300 ease-out origin-top px-3 md:px-6 lg:px-8',
    // the one-line row of the scrolled header (HEADER_HEIGHT.compact - bar)
    scrolled: 'scale-100 opacity-100 h-10',
    normal: 'scale-100 opacity-100 min-h-[100px] md:min-h-[140px] py-3 md:py-6',
    
    info: {
      container: 'flex-shrink-0 flex items-center',
      compact: 'flex items-center gap-4 text-on-header text-sm transition-all duration-300 ease-out',
      title: 'font-bold',
      subtitle: 'flex items-center gap-3 text-xs',
    },
  },
  
  // Service portal button
  servicePortal: {
    base: '', // Empty base since positioning is now handled conditionally in Header
    // vertical padding per state in Header.tsx (smaller in the scrolled row)
    style: 'bg-gradient-to-r from-emerald-500 to-teal-500 dark:from-emerald-400 dark:to-teal-400 text-white font-bold px-6 rounded-full shadow-lg hover:shadow-2xl hover:from-emerald-600 hover:to-teal-600 dark:hover:from-emerald-500 dark:hover:to-teal-500 text-sm border border-emerald-400/30 backdrop-blur-sm',
    hover: 'transition-all duration-300 ease-out hover:scale-110 active:scale-95 transform',
  },
  
  // Language selector
  languageSelector: 'bg-transparent text-white text-base px-2 py-1 focus:outline-none focus:ring-0 border-0 appearance-none cursor-pointer hover:bg-white/10 transition-colors rounded text-right',
  
  // Hamburger menu
  hamburger: {
    button: 'flex items-center gap-2 text-sm text-on-header focus:outline-none',
    icon: 'relative w-8 h-8 flex items-center justify-center',
    line: 'absolute h-0.5 w-6 bg-current rounded transition-all duration-300',
    
    // Line positions for different states
    states: {
      closed: {
        top: 'top-2',
        middle: 'top-4',
        bottom: 'top-6',
      },
      open: {
        top: 'rotate-45 top-4',
        middle: 'opacity-0',
        bottom: '-rotate-45 top-4',
      },
    },
  },
};

// =============================================================================
// SEARCH FIELD STYLES
// =============================================================================

export const searchStyles = {
  // Main container
  container: 'relative flex items-center',
  
  // Search wrapper with transitions
  wrapper: 'relative transition-all duration-300 ease-out flex items-center',
  expanded: 'w-full sm:w-80',
  collapsed: 'w-12',
  
  // Search button
  button: {
    base: 'rounded-full flex items-center justify-center transition-all duration-300 ease-out transform-gpu',
    expanded: 'w-12 h-12 p-2 hover:bg-cockpitblue-light/30 text-white',
    collapsed: 'w-12 h-12 p-2 hover:bg-cockpitblue-light/30 text-white',
  },
  
  // Search input container
  inputContainer: 'flex-1 relative overflow-hidden transition-all duration-300 ease-out',
  
  // Search input
  input: 'flex-1 bg-transparent text-white placeholder-white/70 focus:outline-none focus:ring-0 border-0 text-base',
  
  // Search dropdown
  dropdown: {
    container: 'absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50',
    content: 'max-h-96 overflow-y-auto',
    
    section: {
      header: 'px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide',
      item: 'px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0',
      itemSelected: 'bg-blue-50 text-blue-700',
      itemDefault: 'hover:bg-gray-50 text-gray-700',
    },
    
    // Recent searches
    recent: {
      container: 'px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100',
      content: 'flex items-center gap-4',
      icon: 'w-5 h-5 text-gray-400 flex-shrink-0',
      text: 'text-base font-medium truncate',
    },
    
    // Search suggestions  
    suggestion: {
      container: 'px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0',
      content: 'flex items-center gap-4',
      icon: 'w-5 h-5 flex-shrink-0',
      iconMachine: 'text-blue-500',
      iconLine: 'text-green-500',
      iconDefault: 'text-gray-500',
      text: 'text-base font-medium',
      meta: 'text-xs text-gray-500 mt-1',
    },
  },
  
  // Search tags
  tags: {
    container: 'flex flex-wrap gap-1 mr-2',
    tag: 'inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full',
    removeButton: 'hover:bg-blue-200 rounded-full p-0.5',
    removeIcon: 'w-3 h-3',
  },
};

// =============================================================================
// DATE RANGE PICKER STYLES
// =============================================================================

export const dateRangeStyles = {
  // Main button (theme-aware: content areas in light + dark)
  button: {
    // No `backdrop-blur`: a backdrop-filter promotes the element to its own
    // composited layer, which made this button paint over the sticky top bar on
    // scroll no matter its z-index (the neighbouring ShiftSelector, which has no
    // backdrop-filter, never had the problem). The button is opaque anyway.
    base: 'text-gray-800 dark:text-white font-medium rounded-lg px-4 py-1.5 transition-all duration-300 ease-out cursor-pointer text-sm text-center focus:outline-none focus:ring-0',
    withRange: 'bg-white dark:bg-white/10 border border-gray-300 dark:border-white/30 hover:bg-gray-50 dark:hover:bg-cockpitblue-light/30',
    withoutRange: 'text-orange-700 dark:text-white bg-orange-100 dark:bg-orange-200/20 border-2 border-orange-400 dark:border-orange-400/70 hover:bg-orange-200 dark:hover:bg-orange-200/30 animate-pulse shadow-lg shadow-orange-400/20',
  },
  
  // Icon
  icon: 'w-5 h-5 flex-shrink-0 pointer-events-none',
  
  // Text content
  text: {
    label: 'font-semibold',
    range: 'text-sm opacity-90',
    placeholder: 'text-sm opacity-75',
  },
};

// =============================================================================
// MACHINE AND LINE CARD STYLES
// =============================================================================

export const cardStyles = {
  // Base card styling
  base: 'bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 relative transform-gpu transition-transform duration-300 hover:-rotate-1 hover:scale-105 active:rotate-0.5 active:scale-100',
  
  // Machine card specific
  machine: {
    container: 'bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 relative transform-gpu transition-transform duration-300 hover:-rotate-1 hover:scale-105 active:rotate-0.5 active:scale-100',
    
    icon: {
      container: 'absolute -top-4 right-2',
      style: { zIndex: 2 },
      classes: 'drop-shadow-lg',
    },
    
    info: {
      container: 'pr-24',
      header: 'flex items-center space-x-2 mb-3',
      title: 'font-semibold text-gray-900 text-sm',
    },
    
    details: {
      container: 'text-xs text-gray-600 space-y-1',
      row: '',
      label: 'font-medium',
    },
  },
  
  // Line card specific
  line: {
    container: 'bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 relative transform-gpu transition-transform duration-300 hover:-rotate-1 hover:scale-105 active:rotate-0.5 active:scale-100',
    
    icon: {
      container: 'absolute top-5 right-2',
      style: { zIndex: 2 },
      classes: 'drop-shadow-lg',
    },
    
    info: {
      container: 'pr-20',
      header: 'flex items-center space-x-2 mb-3',
      title: 'font-semibold text-gray-900 text-sm',
    },
    
    details: {
      container: 'text-xs text-gray-600 space-y-1',
      row: '',
      label: 'font-medium',
    },
  },
};

// =============================================================================
// SHIFT SELECTOR STYLES
// =============================================================================

export const shiftStyles = {
  // Main container
  container: 'flex flex-col gap-2',
  
  // Loading state
  loading: {
    container: 'flex items-center gap-2 bg-white/80 rounded-lg px-3 py-2 border border-blue-200',
    spinner: 'animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full',
    text: 'text-sm text-blue-700 font-medium',
  },
  
  // No shifts available
  noShifts: {
    container: 'flex flex-col gap-2',
    message: 'flex items-center justify-center bg-gray-50 rounded-lg px-3 py-2 border border-gray-200',
    text: 'text-sm text-gray-500 font-medium',
  },
  
  // Button group
  buttonGroup: {
    container: 'flex gap-2 bg-white/80 rounded-lg px-2 py-1 border border-blue-200',
    
    selectAll: {
      base: 'px-2 py-1 rounded text-xs font-bold border transition-all',
      allSelected: 'bg-green-600 text-white border-green-700 shadow',
      someSelected: 'bg-yellow-500 text-white border-yellow-600 shadow',
      noneSelected: 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50',
    },
    
    shift: {
      base: 'px-2 sm:px-3 py-0.5 sm:py-1 rounded font-bold border text-[10px] sm:text-xs font-mono transition-all',
      selected: 'bg-brand text-on-brand border-brand shadow',
      unselected: 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50',
    },
  },
  
  // Error state
  error: {
    container: 'text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded border border-orange-200',
  },
};

// =============================================================================
// TAB COMPONENT STYLES
// =============================================================================

export const tabStyles = {
  // Main tab container
  container: 'grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-1.5 sm:gap-2 lg:gap-4 mb-4 sm:mb-6',
  
  // Individual tab
  tab: {
    base: 'group relative px-2 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 rounded-lg sm:rounded-xl font-medium text-xs sm:text-sm lg:text-base transition-all duration-300 ease-out transform hover:scale-[1.02] hover:-translate-y-0.5 backdrop-blur-sm border overflow-hidden',
    active: 'bg-gradient-to-br from-brand-bright to-brand text-on-brand border-brand/30 shadow-lg shadow-brand/25 ring-2 ring-brand/20',
    inactive: 'bg-white/60 border-gray-200/50 text-gray-700 hover:bg-white/80 hover:border-gray-300/60 hover:text-gray-900 hover:shadow-md',
    
    // Effects
    glow: 'absolute inset-0 bg-gradient-to-br from-brand-bright/20 to-brand/20 rounded-xl blur-sm scale-110',
    content: 'relative z-10 truncate font-semibold tracking-tight',
    indicator: 'absolute bottom-0 left-1/2 -translate-x-1/2 h-1 rounded-t-full transition-all duration-300 ease-out',
    indicatorActive: 'w-8 bg-on-brand/70 shadow-sm',
    indicatorInactive: 'w-0 bg-transparent group-hover:w-4 group-hover:bg-gray-300',
    shine: 'absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out',
  },
  
  // Sub-tabs (flat style)
  subTabs: {
    container: 'flex border-b border-gray-200',
    tab: 'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
    active: 'text-blue-600 border-blue-600',
    inactive: 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300',
  },
  
  // Chrome-style tabs
  chromeTabs: {
    container: 'flex bg-slate-200 border-b border-slate-300 relative',
    tab: 'px-4 py-2 border border-slate-300 border-b-0 rounded-t-lg mr-1 min-w-[100px] max-w-[200px] transition-colors duration-150 flex items-center justify-center relative',
    active: 'bg-white text-slate-800 shadow-md border-slate-300',
    inactive: 'bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-50',
    
    content: 'flex items-center justify-center w-full',
    label: 'truncate max-w-[100px]',
    closeButton: 'ml-2 w-4 h-4 flex items-center justify-center rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700',
    closeIcon: 'w-3 h-3',
    bottomBorder: 'absolute left-0 right-0 bottom-0 h-px bg-slate-300 z-0',
  },
};

// =============================================================================
// VIEW MODE SELECTOR STYLES
// =============================================================================

export const viewModeStyles = {
  container: 'flex bg-gray-100 rounded-lg p-1',
  button: {
    base: 'px-4 py-2 rounded-md text-sm font-medium transition-colors',
    active: 'bg-white text-blue-600 shadow-sm',
    inactive: 'text-gray-600 hover:text-gray-900',
  },
};

export default {
  header: headerStyles,
  search: searchStyles,
  dateRange: dateRangeStyles,
  card: cardStyles,
  shift: shiftStyles,
  tabs: tabStyles,
  viewMode: viewModeStyles,
};
